import { duration } from '@eturi/date-util'
import type { Simplify } from '@eturi/ts-helpers'
import { ImmutSet } from '@eturi/util'
import type { InstallChildAppArg, SThunkState, WithRuleState, WithAppsState } from '@op/services'
import {
	bindCreateAsyncThunkToState,
	castImmutSet,
	installChildApp,
	refreshUserApps,
	resetAction,
} from '@op/services'
import type { PayloadAction } from '@reduxjs/toolkit'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import identity from 'lodash/identity.js'
import { createSliceTransformer } from 'rtk-slice-transformer'

/**
 * The purpose of this is to persist certain command calls that we don't want to spam. This allows
 * us to ensure that throttling isn't limited to the component lifecycle. We can open and close
 * child app install modals and the state persists. We can switch users and devices, etc.
 */
export type RequestTrackingState = {
	readonly childApp: {
		// Immutable set of device ids
		readonly [userId: string]: ImmutSet<string>
	}

	// Immutable set of user ids
	readonly refreshApps: ImmutSet<string>

	// Immutable set of user ids that have been refreshed in the current session
	readonly sessionRefreshApps: ImmutSet<string>
}

export type WithRequestTrackingState = {
	readonly reqTracking: RequestTrackingState
}

const initialState: RequestTrackingState = {
	childApp: {},
	refreshApps: new ImmutSet(),
	sessionRefreshApps: new ImmutSet(),
}

export const requestTrackingSlice = createSlice({
	name: 'reqTracking',
	initialState,
	reducers: {
		clearChildAppRequest(s, { payload: { deviceId, userId } }: PayloadAction<InstallChildAppArg>) {
			const childApp = castImmutSet<RequestTrackingState['childApp']>(s.childApp)
			const deviceIds = childApp[userId]

			if (!deviceIds) return

			const nextDeviceIds = deviceIds.delete(deviceId)

			if (!nextDeviceIds.size) {
				delete childApp[userId]
			} else {
				childApp[userId] = nextDeviceIds
			}
		},

		clearRefreshAppsRequest(_s, { payload: userId }: PayloadAction<string>) {
			const s = castImmutSet<RequestTrackingState>(_s)
			s.refreshApps = s.refreshApps.delete(userId)
		},

		setChildAppRequest(s, { payload: { deviceId, userId } }: PayloadAction<InstallChildAppArg>) {
			const deviceIds = s.childApp[userId] ?? new ImmutSet()
			s.childApp[userId] = deviceIds.add(deviceId)
		},

		setRefreshAppsRequest(_s, { payload: userId }: PayloadAction<string>) {
			const s = castImmutSet<RequestTrackingState>(_s)
			s.refreshApps = s.refreshApps.add(userId)
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(resetAction, () => initialState)
			.addCase(requestRefreshApps.fulfilled, (s, a) => {
				const { isSessionReq, userId } = a.meta.arg

				if (isSessionReq) {
					s.sessionRefreshApps = s.sessionRefreshApps.add(userId)
				}
			})
	},
})

const { clearChildAppRequest, clearRefreshAppsRequest, setChildAppRequest, setRefreshAppsRequest } =
	requestTrackingSlice.actions

export const requestTrackingSliceTransformer = createSliceTransformer(
	requestTrackingSlice,
	identity,
)

type RequestTrackingThunkState = Simplify<
	SThunkState & WithAppsState & WithRuleState & WithRequestTrackingState
>

const createAsyncThunk = bindCreateAsyncThunkToState<RequestTrackingThunkState>()

export const requestInstallChildApp = createAsyncThunk(
	'requestTracking/requestInstallChildApp',
	async (installArg: InstallChildAppArg, { dispatch }) => {
		let timeoutId = -1

		try {
			dispatch(setChildAppRequest(installArg))
			timeoutId = setTimeout(
				() => {
					dispatch(clearChildAppRequest(installArg))
				},
				duration(45, 's'),
			)

			await dispatch(installChildApp(installArg)).unwrap()
		} catch (e) {
			dispatch(clearChildAppRequest(installArg))
			clearTimeout(timeoutId)
			throw e
		}
	},
	{
		condition: (arg, api) => {
			if (isInstallingChildApp$(api.getState(), arg)) return false
		},
	},
)

type RequestRefreshAppsArg = {
	readonly userId: string
	// "Session" requests happen once per user per session and aren't subject to the same
	// timeout throttling as other requests.
	readonly isSessionReq?: boolean
}

export const requestRefreshApps = createAsyncThunk(
	'requestTracking/requestRefreshApps',
	async ({ userId, isSessionReq = false }: RequestRefreshAppsArg, { dispatch }) => {
		let timeoutId = -1

		try {
			if (!isSessionReq) {
				dispatch(setRefreshAppsRequest(userId))
				timeoutId = setTimeout(
					() => {
						dispatch(clearRefreshAppsRequest(userId))
					},
					duration(1, 'm'),
				)
			}

			await dispatch(refreshUserApps(userId)).unwrap()
		} catch (e) {
			dispatch(clearRefreshAppsRequest(userId))
			clearTimeout(timeoutId)
			throw e
		}
	},
	{
		condition: ({ userId, isSessionReq }, api) => {
			// Don't make the session request if it's already been made.
			if (isSessionReq && state$(api.getState()).sessionRefreshApps.has(userId)) return false
			if (isRefreshingApps$(api.getState(), userId)) return false
		},
	},
)

const state$ = <T extends WithRequestTrackingState>(s: T) => s.reqTracking

const requestChildAppState$ = createSelector(state$, (s) => s.childApp)
const requestRefreshAppsState$ = createSelector(state$, (s) => s.refreshApps)

export const isInstallingChildApp$ = createSelector(
	requestChildAppState$,
	(_: unknown, arg: InstallChildAppArg) => arg,
	(childApp, arg) => childApp[arg.userId]?.has(arg.deviceId) ?? false,
)

export const isRefreshingApps$ = createSelector(
	requestRefreshAppsState$,
	(_: unknown, userId: Maybe<string>) => userId,
	(refreshApps, userId) => userId != null && refreshApps.has(userId),
)
